import React from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  SectionHead,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import {
  downloadInventoryLedgerXlsx,
  ledgerDayLabel,
  ledgerMonthLabel as monthLabel,
  reorderDateNote,
  ledgerStockNotes,
  poArrivalsOf,
} from "../../utils/inventory-ledger-xlsx-export";

const fmt = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Math.round(Number(v)).toLocaleString("en-US");
// Signed, for moves that can go either way (transfers in / out).
const fmtSigned = (v) =>
  v == null || Number.isNaN(Number(v)) || Math.round(Number(v)) === 0
    ? fmt(v)
    : `${Number(v) > 0 ? "+" : "−"}${fmt(Math.abs(Number(v)))}`;

const leadText = (ledger) => {
  const m = ledger.lead_time_months;
  return `${ledger.lead_time_days} days lead time${m ? ` (~${m} month${m > 1 ? "s" : ""})` : ""}`;
};

/**
 * Month-by-month inventory ledger for one Updated Forecast run (`run.ledger`,
 * built by the server — see control-tower forecast/inventory-ledger.js): past
 * stock and sales, today, then the forecast with open POs, the projected
 * stock and the recommended POs, shown in the month they arrive (placed lead
 * time earlier). Downloadable as an Excel sheet.
 */
export default function InventoryLedgerSection({ ledger, sku }) {
  const ui = useMetricsUi();
  const { colors } = ui;
  if (!ledger?.months?.length) return null;
  const months = ledger.months;
  const positive = { backgroundColor: ui.positiveBg, color: ui.positive, fontWeight: 700 };
  const currentCol = { backgroundColor: ui.accentSoft };
  const warehouses = (ledger.warehouses || []).filter((w) => w.pct > 0);
  const arrivals = poArrivalsOf(ledger);
  const stockNotes = ledgerStockNotes(ledger);
  // Out-of-stock months: on hand and sales cells get a marker + the reason.
  const stockoutSx = (m) => (stockNotes.cellNote(m) ? { color: ui.danger, fontWeight: 700 } : {});
  // A cell's tooltip: its own text plus the stock notes, one point each.
  const withNote = (m, text, { moves = false } = {}) => {
    const items = [...stockNotes.cellItems(m), ...(moves ? stockNotes.moveItems(m) : [])];
    if (!items.length) return text;
    return [...(text ? [text] : []), ...items];
  };
  const hasOrders = arrivals.size > 0;
  // The next PO to place: the earliest arrival's reorder date.
  const nextArrival = hasOrders ? arrivals.get([...arrivals.keys()].sort()[0]) : null;
  const shareSum = warehouses.reduce((s, w) => s + w.pct, 0) || 1;

  const cell = (m, extra = {}) =>
    numCellSx(ui, {
      whiteSpace: "nowrap",
      ...(m.kind === "current" && currentCol),
      ...extra,
    });
  const labelCell = (extra = {}) =>
    textCellSx(ui, {
      position: "sticky",
      left: 0,
      zIndex: 1,
      backgroundColor: colors.bgPaper,
      whiteSpace: "nowrap",
      fontWeight: 600,
      ...extra,
    });

  const rows = [
    {
      key: "on_hand",
      label: "On hand (month end)",
      hint: "From the daily inventory snapshot. Past: the last snapshot of the month. This month: the latest snapshot. Later: projected.",
      value: (m) => m.on_hand,
      title: (m) =>
        m.kind === "past" && m.on_hand == null
          ? "No daily inventory snapshot for this month."
          : m.kind === "current" && m.on_hand_month_end != null
          ? withNote(m, `Today. Heading for ${fmt(m.on_hand_month_end)} by month end.`, { moves: true })
          : m.kind === "future"
            ? "Projected"
            : withNote(m, undefined, { moves: true }),
      sx: (m) =>
        m.kind === "future"
          ? m.on_hand != null && m.on_hand < (ledger.safety_stock || 0)
            ? { color: ui.danger }
            : {}
          : m.stockout?.out_at_end || (m.on_hand != null && Math.round(m.on_hand) === 0)
            ? stockoutSx(m)
            : {},
      marker: (m) => m.kind !== "future" && Boolean(m.stockout?.days),
      flag: (m) => m.kind !== "future" && Boolean(m.unexplained_moves?.events?.length),
    },
    {
      key: "po",
      label: "POs arrived / arriving",
      hint: "Past months: PO units received (ASN receipts). This month and later: open POs by expected arrival month; overdue ones count this month.",
      value: (m) => (m.kind === "past" ? m.po_received ?? null : m.po_inbound ?? null),
      title: (m) =>
        m.kind === "past"
          ? "Received"
          : m.kind === "current"
            ? `Still due this month. Received so far: ${fmt(m.po_received ?? 0)} (already in on hand).`
            : "Expected to arrive",
    },
    {
      key: "transfers",
      label: "Transfers in / out",
      hint: "Transfer orders. Past months: units received from warehouses outside this forecast. This month and later: + arriving / − leaving these warehouses (moves between them only count while on the road).",
      value: (m) => (m.kind === "past" ? m.transfer_received ?? null : m.transfer_net ?? null),
      format: fmtSigned,
      sx: (m) => (m.kind !== "past" && m.transfer_net < 0 ? { color: ui.danger } : {}),
    },
    {
      key: "sales",
      label: "Sales",
      value: (m) => m.sales,
      title: (m) => withNote(m, m.kind === "current" ? "So far this month." : undefined),
      sx: stockoutSx,
    },
    ...(months.some((m) => m.shortfall > 0)
      ? [
          {
            key: "shortfall",
            label: "Shortfall",
            hint: "Forecast demand the stock can't cover that month. It's lost, not carried into later months.",
            value: (m) => (m.shortfall > 0 ? m.shortfall : null),
            sx: (m) => (m.shortfall > 0 ? { color: ui.danger, fontWeight: 700 } : {}),
          },
        ]
      : []),
    {
      key: "forecast",
      label: "Forecast",
      value: (m) => m.forecast,
    },
  ];

  return (
    <Box>
      <SectionHead
        title="Inventory Ledger"
        right={
          <Button
            size="small"
            variant="outlined"
            onClick={() => downloadInventoryLedgerXlsx(ledger, { sku })}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Download Excel
          </Button>
        }
      />
      <Box sx={{ ...ui.card, p: "10px 16px", mb: 1.25, textAlign: "left" }}>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {[
            `All warehouses in this forecast. On hand comes from the daily inventory snapshot${ledger.stock_as_of ? ` (latest ${new Date(`${ledger.stock_as_of}T00:00:00Z`).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })})` : ""}; incoming stock from open POs.`,
            `PO arrival is the recommended order, shown in the month it arrives (green): place it ${leadText(ledger)} earlier.`,
            `It's sized after open POs and transfers are counted, and keeps at least ${fmt(ledger.safety_stock)} units of safety stock${ledger.min_order_qty ? `, at least ${fmt(ledger.min_order_qty)} per order` : ""}.`,
            "On hand drops when an order ships, not when it's placed.",
            "Red marks months the SKU was out of stock; ! marks a stock change with no shipment or PO receipt behind it. Hover a marked cell for the details.",
          ].map((t) => (
            <Typography key={t} component="li" sx={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.6, textAlign: "left" }}>
              {t}
            </Typography>
          ))}
        </Box>
      </Box>
      {stockNotes.bannerItems.length > 0 && (
        <Box
          role="status"
          sx={{ ...ui.card, p: "10px 16px", mb: 1.25, borderLeft: `4px solid ${ui.danger}`, textAlign: "left" }}
        >
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {stockNotes.bannerItems.map((t, i) => (
              <Typography key={i} component="li" sx={{ fontSize: 13, color: colors.textPrimary, lineHeight: 1.6, textAlign: "left" }}>
                {t}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      <TableContainer sx={{ ...ui.card, p: 0, overflowX: "auto" }}>
        <Table size="small" data-testid="inventory-ledger">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx(ui, { position: "sticky", left: 0, zIndex: 2 })} />
              {months.map((m) => (
                <TableCell
                  key={m.month}
                  align="right"
                  sx={headCellSx(ui, m.kind === "current" ? { ...currentCol, color: ui.accent } : {})}
                >
                  {monthLabel(m.month)}
                  {m.kind === "current" && (
                    <Box component="span" sx={{ display: "block", fontSize: 9.5, letterSpacing: "0.04em" }}>
                      today
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key}>
                <TableCell sx={labelCell()} title={r.hint}>
                  {r.label}
                </TableCell>
                {months.map((m) => {
                  const tip = r.title ? r.title(m) : undefined;
                  const content = (
                  <TableCell
                    key={m.month}
                    sx={cell(m, r.sx ? r.sx(m) : {})}
                    title={Array.isArray(tip) ? undefined : tip}
                    data-notes={Array.isArray(tip) ? tip.join(" ") : undefined}
                  >
                    {(r.format || fmt)(r.value(m))}
                    {r.marker && r.marker(m) && (
                      <Box
                        component="span"
                        data-testid="stockout-marker"
                        sx={{ display: "inline-block", ml: 0.5, px: 0.5, borderRadius: "4px", fontSize: 9.5, fontWeight: 700, lineHeight: "14px", color: "#fff", backgroundColor: ui.danger, verticalAlign: "middle" }}
                      >
                        {m.stockout.days}d out
                      </Box>
                    )}
                    {r.flag && r.flag(m) && (
                      <Box
                        component="span"
                        data-testid="unexplained-move"
                        sx={{ display: "inline-block", ml: 0.5, width: 14, height: 14, borderRadius: "50%", fontSize: 10, fontWeight: 700, lineHeight: "14px", textAlign: "center", color: "#fff", backgroundColor: ui.warning, verticalAlign: "middle" }}
                      >
                        !
                      </Box>
                    )}
                  </TableCell>
                  );
                  return Array.isArray(tip) ? (
                    <Tooltip
                      key={m.month}
                      arrow
                      placement="top"
                      title={
                        <Box component="ul" sx={{ m: 0, pl: 2, py: 0.25 }}>
                          {tip.map((t, i) => (
                            <Box component="li" key={i} sx={{ fontSize: 12, lineHeight: 1.5, mb: 0.25 }}>
                              {t}
                            </Box>
                          ))}
                        </Box>
                      }
                    >
                      {content}
                    </Tooltip>
                  ) : (
                    content
                  );
                })}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={labelCell({ fontWeight: 700 })}>
                PO arrival
                <Box data-testid="po-arrival-detail" sx={{ fontSize: 11, fontWeight: 400, lineHeight: 1.4, mt: 0.25, color: colors.textSecondary }}>
                  {nextArrival && (
                    <Box
                      title={reorderDateNote(nextArrival)}
                      sx={nextArrival.overdue ? { color: ui.danger, fontWeight: 600 } : {}}
                    >
                      Reorder Date: {ledgerDayLabel(nextArrival.reorder_date)}
                    </Box>
                  )}
                  <Box>{fmt(ledger.lead_time_days)} days of lead time</Box>
                </Box>
              </TableCell>
              {months.map((m) => {
                const a = arrivals.get(m.month);
                return (
                  <TableCell
                    key={m.month}
                    sx={cell(m, { fontWeight: 700, ...(a && positive) })}
                    title={a ? `Order by ${a.order_by.map(monthLabel).join(", ")}` : undefined}
                  >
                    {m.kind === "past" ? "—" : fmt(a ? a.qty : 0)}
                  </TableCell>
                );
              })}
            </TableRow>
            {hasOrders &&
              warehouses.map((w) => (
                <TableRow key={w.id}>
                  <TableCell sx={labelCell({ fontWeight: 400, pl: 3 })}>
                    {w.name || "Warehouse"} ({Math.round((w.pct / shareSum) * 100)}%)
                  </TableCell>
                  {months.map((m) => (
                    <TableCell key={m.month} sx={cell(m, { color: colors.textSecondary })}>
                      {arrivals.has(m.month) ? fmt(arrivals.get(m.month).by_warehouse[w.id] ?? 0) : "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
